import * as xlsx from "xlsx";
import { IPlanilhaCriarLote } from "../interfaces/IPlanilhaCriarLote";
import { IPlanilhaCriarLoteLeitura } from "../interfaces/IPlanilhaCriarLoteLeitura";

interface IResultadoLerPlanilha {
  temErro: boolean;
  mensagemErro: string;
  dados: IPlanilhaCriarLote[];
}
export const lerPlanilha = async (
  arquivo: File,
): Promise<IResultadoLerPlanilha> => {
  const buffer = await arquivo.arrayBuffer();
  const workbook = xlsx.read(new Uint8Array(buffer), {
    type: "array",
    cellDates: true,
  });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const dados: IPlanilhaCriarLoteLeitura[] = xlsx.utils.sheet_to_json(
    worksheet,
    {
      skipHidden: true,
    },
  );

  if (!dados || !dados.length) {
    return {
      temErro: true,
      mensagemErro: "Não foram encontrados dados na planilha",
      dados: [],
    };
  }

  if (
    dados.some(
      (item) =>
        !item?.RENAVAM ||
        !item?.CHASSI ||
        !item?.PLACA ||
        !item?.UF
    )
  ) {
    return {
      temErro: true,
      mensagemErro:
        "Todos os veículos da planilha precisam ter Renavam, chassi, placa e UF",
      dados: [],
    };
  }

  const renavams = dados.map((item) => normalizarRenavam(item.RENAVAM));
  if (new Set(renavams).size !== renavams.length) {
    return {
      temErro: true,
      mensagemErro: "A planilha não pode ter Renavam duplicado",
      dados: [],
    };
  }

  const placas = dados.map((item) => normalizarTexto(item.PLACA));
  if (new Set(placas).size !== placas.length) {
    return {
      temErro: true,
      mensagemErro: "A planilha não pode ter placa duplicada",
      dados: [],
    };
  }

  const chassis = dados.map((item) => normalizarTexto(item.CHASSI));
  if (new Set(chassis).size !== chassis.length) {
    return {
      temErro: true,
      mensagemErro: "A planilha não pode ter chassi duplicado",
      dados: [],
    };
  }

  for (let i = 0; i < dados.length; i++) {
    const item = dados[i];
    const linha = i + 2;

    const renavam = normalizarRenavam(item.RENAVAM);
    const placa = normalizarTexto(item.PLACA);
    const chassi = normalizarTexto(item.CHASSI);
    const uf = normalizarTexto(item.UF);

    if (!validarRenavam(renavam)) {
      return {
        temErro: true,
        mensagemErro: `Renavam inválido na linha ${linha}: ${item.RENAVAM ?? ""}`,
        dados: [],
      };
    }

    if (!validarChassi(chassi)) {
      return {
        temErro: true,
        mensagemErro: `Chassi inválido na linha ${linha}: ${item.CHASSI ?? ""}`,
        dados: [],
      };
    }

    if (!validarPlaca(placa)) {
      return {
        temErro: true,
        mensagemErro: `Placa inválida na linha ${linha}: ${item.PLACA ?? ""}`,
        dados: [],
      };
    }

    if (!validarUF(uf)) {
      return {
        temErro: true,
        mensagemErro: `UF inválida na linha ${linha}: ${item.UF ?? ""}`,
        dados: [],
      };
    }
  }

  return {
    temErro: false,
    mensagemErro: "",
    dados: dados.map((item) => {
      return {
        renavam: normalizarRenavam(item.RENAVAM),
        placa: normalizarTexto(item.PLACA),
        chassi: normalizarTexto(item.CHASSI),
        uf: normalizarTexto(item.UF),
      };
    }),
  };
};

const regexRenavam = /^[0-9]{11}$/;
const regexChassi = /^[A-HJ-NPR-Z0-9]{17}$/i;
const regexPlacaAntiga = /^[A-Z]{3}[0-9]{4}$/;
const regexPlacaMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;

const UFS_VALIDAS = ["PR"];

const normalizarRenavam = (renavam: string | number) =>
  String(renavam).replace(/\D/g, "").padStart(11, "0");

const normalizarTexto = (valor: string | undefined | null) =>
  String(valor ?? "")
    .trim()
    .toUpperCase();

export const validarRenavam = (renavam: string) => regexRenavam.test(renavam);

export const validarChassi = (chassi: string) => regexChassi.test(chassi);

export const validarPlaca = (placa: string) =>
  regexPlacaAntiga.test(placa) || regexPlacaMercosul.test(placa);

export const validarUF = (uf: string) => UFS_VALIDAS.includes(uf.toUpperCase());
